import {
  Activity, BarChart3, Bluetooth, CalendarDays, ClipboardCheck,
  ClipboardList, Gauge, Lock, Route, AlertTriangle,
} from "lucide-react";

/**
 * Single source of truth for the Reports navigator.
 * Groups are rendered as sections in the left rail.
 * Each report's `id` is the active selection key + the localStorage
 * key for "recents".
 */
export const REPORTS = {
  // ---- GPS Lock group ----
  GPS_LOCK_UNLOCK: {
    id: "GPS_LOCK_UNLOCK",
    group: "GPS Lock",
    label: "Lock / Unlock",
    icon: Lock,
    description: "History of every lock and unlock event from GPS lock devices. Filter by asset, date, event type. Each row includes GPS coordinates of the event — click the map icon to open it.",
    permission: null,
  },
  TASK_REPORT: {
    id: "TASK_REPORT",
    group: "GPS Lock",
    label: "Task Report",
    icon: ClipboardList,
    description: "Tasks assigned to drivers / field agents with completion status, timestamps, and GPS location. Verify scheduled visits or deliveries were completed on time and at the correct site.",
    permission: null,
  },
  ASSET_TRACKING: {
    id: "ASSET_TRACKING",
    group: "GPS Lock",
    label: "Asset Tracking",
    icon: BarChart3,
    description: "Movement history of tracked assets. Each row shows position, speed, and status at the recorded time. Useful for auditing where an asset has been without relying on live tracking.",
    permission: null,
  },

  // ---- BT Lock group ----
  BT_LOCK_UNLOCK: {
    id: "BT_LOCK_UNLOCK",
    group: "BT Lock",
    label: "Lock / Unlock",
    icon: Bluetooth,
    description: "Lock and unlock events captured via Bluetooth locks. Each entry records the triggering user / device, timestamp, and BT lock identifier. Audit access history for BT-secured compartments.",
    permission: null,
  },

  // ---- GPS Tracker group ----
  GT06_TRIPS: {
    id: "GT06_TRIPS",
    group: "GPS Tracker",
    label: "Trips",
    icon: Route,
    description: "Every trip (ignition-on journey) in the selected window — start/end, duration, distance, top and average speed, idle time and alerts. Pick one or more trackers (or none for all); click a trip to draw its exact GPS route on the map. Export to Excel or PDF. (Replaces the separate Trip Route and Trip Summary reports.)",
    permission: null,
  },
  GT06_DAILY_ACTIVITY: {
    id: "GT06_DAILY_ACTIVITY",
    group: "GPS Tracker",
    label: "Daily Activity",
    icon: CalendarDays,
    description: "Each vehicle's activity rolled up to one row per day — total distance, engine hours, moving vs idle time, trip count. Good for comparing driver / vehicle productivity.",
    permission: null,
  },
  GT06_FLEET_DISTANCE: {
    id: "GT06_FLEET_DISTANCE",
    group: "GPS Tracker",
    label: "Fleet Distance",
    icon: Gauge,
    description: "Total distance, engine hours, moving / idle time, trip count, top speed for every tracker in the fleet for the selected window. Export to Excel or PDF.",
    permission: null,
  },
  GT06_ALERT_LOG: {
    id: "GT06_ALERT_LOG",
    group: "GPS Tracker",
    label: "Alert Log",
    icon: AlertTriangle,
    description: "Paginated, searchable history of fleet events: engine on/off, overspeed, low battery, tamper, GPS lost. Filter by device, alert type, or severity. Each row links to the GPS location.",
    permission: null,
  },
  GT06_FRAME_LOG: {
    id: "GT06_FRAME_LOG",
    group: "GPS Tracker",
    label: "Frame Log",
    icon: Activity,
    description: "Raw GPS frame log — every location report received from the device, one row per packet. Diagnostics: GPS fix quality, satellite count, signal strength. Not for fleet-level analysis.",
    permission: null,
  },

  // ---- Audit ----
  AUDIT_TRAIL: {
    id: "AUDIT_TRAIL",
    group: "Audit",
    label: "Audit Trail",
    icon: ClipboardCheck,
    description: "Tenant audit log — who did what, when. Filter by user, action type, entity, and date range. Export for compliance review.",
    permission: ["auditLog_viewClientLogs", "auditLog_viewPlatformLogs"],
  },
};

export const REPORT_GROUPS = ["GPS Lock", "BT Lock", "GPS Tracker", "Audit"];

export const ALL_REPORT_IDS = Object.keys(REPORTS);

/**
 * Retired report ids -> their replacement. Remembered tabs / recents saved in localStorage before a
 * merge still resolve to the right report instead of silently falling back to the group's first tab.
 */
export const LEGACY_REPORT_IDS = {
  GT06_TRIP_ROUTE: "GT06_TRIPS",
  GT06_TRIP_SUMMARY: "GT06_TRIPS",
};

export function resolveReportId(id) {
  return LEGACY_REPORT_IDS[id] || id;
}

export const DEFAULT_REPORT = "GPS_LOCK_UNLOCK";

const RECENTS_KEY = "yk_reports_recents_v1";

export function readRecents() {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return [...new Set(arr.map(resolveReportId))].filter((id) => REPORTS[id]);
  } catch {
    return [];
  }
}

export function pushRecent(id) {
  if (!REPORTS[id]) return;
  const next = [id, ...readRecents().filter((x) => x !== id)].slice(0, 3);
  try { localStorage.setItem(RECENTS_KEY, JSON.stringify(next)); } catch {/* ignore */}
}
